import {
    AjaxPaginationControllerApi,
    ThoughtInSearchDTO,
    DiscussionWithoutContentDTO,
    DiscussionWithoutContentDTOInSeries,
    GroupDTO,
    SeriesDTO,
    UserProfileDTO,
} from "@/generated-client";
import { isBookmarksSeries } from "@/main/react/series/BookmarksSeriesLocalization";

// `images` (round 12 `1i`/`2e`): the profile Images tab, the authored list narrowed to posts with media.
// SV-147 A8 retired `replies` (the profile Replies tab): the server answers it with the Posts list.
export type UserDiscussionQueryMode = "authored" | "active" | "archived" | "deleted" | "agreed" | "disagreed" | "participated" | "images";
/**
 * Which languages a profile/dashboard discussion list shows. "all" shows the person's posts in
 * every language; "mine" — the picker's "Your languages" — narrows to the viewer's whole reading
 * set, the same set the home feed matches on (changed 2026-07-29; see
 * designs/01-now/2026-07-29-your-languages-reading-set-scope-design.md). The default is
 * caller-controlled: another user's profile defaults to "mine", the own dashboard defaults to
 * "all". Sent to the backend as the `languageScope` query param.
 */
export type UserDiscussionLanguageScope = "all" | "mine";
// "bookmarked" (FI-582) is the viewer's saved replies, owner-only server-side; only the dashboard's
// Saved subtab asks for it, never getDashboardArgumentQueryMode.
export type UserThoughtQueryMode = "authored" | "agreed" | "disagreed" | "deleted" | "bookmarked";
export type UserGroupScope = "leader" | "member";
export type UserSeriesVisibility = "public" | "all";
export type UserGroupsVisibility = "visible" | "all";

export type ProfilePagePaginationApi = Pick<
    AjaxPaginationControllerApi,
    | "newFollowersForUser"
    | "newFollowingsForUser"
    | "userThoughts"
    | "userDiscussions"
    | "userGroups"
    | "userSeries"
    | "discussionsInSeries"
>;

/**
 * True only when the caller is narrowing to the viewer's own language. The profile Groups/Series
 * lists send `languageScope` via the **axios seam** (`options.params`) because the generated client
 * has no `languageScope` param for `userGroups`/`userSeries` — mirroring the search path. The seam is
 * applied only when narrowing, so a default ("all") request stays byte-identical to before this
 * filter existed (no extra positional args, no query param). The Thoughts tab is intentionally not
 * language-scoped (see designs/01-now/2026-07-21-profile-language-scope-thoughts-504-fix.md).
 */
function isNarrowingLanguageScope(languageScope?: UserDiscussionLanguageScope): boolean {
    return Boolean(languageScope && languageScope !== "all");
}

/**
 * The member's narrowing text, as the typed `query` argument of the generated client.
 *
 * <p>`DASHBOARD-23`. The backend takes `?query=` on all four `user/ordered` endpoints (batch 1's
 * `CollectionQueryFilterSql`). Until the 2026-09-14 client refresh it rode the axios seam
 * (`options.params`), the same way `languageScope` did before its own regeneration; it is now an
 * ordinary positional parameter, and the wire request is unchanged (`query=` on the same URL).
 *
 * <p><b>Returns `undefined` when there is nothing to narrow by</b>, so an unfiltered request is
 * byte-identical to the one this surface sent before the box existed — no extra parameter, no
 * `query=`. That is the same "only when narrowing" contract the two language-scope callers keep, and
 * it is what makes this change invisible to every cached client and every existing spec.
 *
 * <p>Trimmed and length-capped to mirror `CollectionQueryFilterSql`'s own 100-character cap, so the
 * client cannot send something the server will silently truncate.
 */
const COLLECTION_QUERY_MAX_LENGTH = 100;

function collectionQuery(query?: string | null): string | undefined {
    const trimmed = (query ?? "").trim().slice(0, COLLECTION_QUERY_MAX_LENGTH);
    return trimmed ? trimmed : undefined;
}

export function getDashboardDiscussionQueryMode(section: string): UserDiscussionQueryMode {
    switch (section) {
        case "authored":
            return "active";
        case "archived":
            return "archived";
        case "deleted":
            return "deleted";
        case "agreement":
            return "agreed";
        case "disagreement":
            return "disagreed";
        case "participated":
            return "participated";
        default:
            return "authored";
    }
}

export function getDashboardArgumentQueryMode(section: string): UserThoughtQueryMode {
    switch (section) {
        case "agreed":
            return "agreed";
        case "disagreed":
            return "disagreed";
        // Owner-only, and enforced server-side rather than here: a non-owner asking for it is clamped
        // to "authored" by UserCollectionPaginationService before the repository sees the string.
        case "deleted":
            return "deleted";
        default:
            return "authored";
    }
}

export async function fetchUserDiscussionsPage(
    api: ProfilePagePaginationApi,
    params: {
        username: string;
        mode: UserDiscussionQueryMode;
        orderingStyle?: string;
        paginationParameter?: string;
        languageScope?: UserDiscussionLanguageScope;
        /** `DASHBOARD-23` — the member's narrowing text, matched server-side on title and excerpt. */
        query?: string;
    },
): Promise<DiscussionWithoutContentDTO[]> {
    // The backend defaults this surface to "all", so only send the param when narrowing to "mine".
    // This keeps default requests byte-identical and avoids passing the extra params positionally.
    const query = collectionQuery(params.query);
    const narrowingLanguage = isNarrowingLanguageScope(params.languageScope);
    // Three branches rather than one call with trailing `undefined`s, on purpose: a request that
    // narrows by neither dimension must be the SAME CALL it was before either filter existed, not just
    // the same HTTP request. The specs that pin these argument lists are the contract.
    const response = query
        ? await api.userDiscussions(
              params.username,
              params.mode,
              params.orderingStyle,
              params.paginationParameter,
              undefined,
              narrowingLanguage ? params.languageScope : undefined,
              query,
          )
        : narrowingLanguage
            ? await api.userDiscussions(
                  params.username,
                  params.mode,
                  params.orderingStyle,
                  params.paginationParameter,
                  undefined,
                  params.languageScope,
              )
            : await api.userDiscussions(
                  params.username,
                  params.mode,
                  params.orderingStyle,
                  params.paginationParameter,
              );
    return response.data ?? [];
}

export async function fetchUserArgumentsPage(
    api: ProfilePagePaginationApi,
    params: {
        username: string;
        mode: UserThoughtQueryMode;
        orderingStyle?: string;
        paginationParameter?: string;
        /**
         * `DASHBOARD-23` — matched server-side on the parent discussion's title and the thought's
         * body. The body is raw `html_content`, so a query that is also a tag name matches broadly;
         * batch 1 accepted that rather than putting a `regexp_replace` on the one list already known
         * to time out for high-activity authors.
         */
        query?: string;
    },
): Promise<ThoughtInSearchDTO[]> {
    // The Thoughts tab is intentionally NOT language-scoped: a thought has no locale of its own, so a
    // "mine" filter would narrow by the parent discussion's locale via a computed, non-sargable
    // expression that times out (504) for high-activity authors. Always fetch every authored argument.
    // See designs/01-now/2026-07-21-profile-language-scope-thoughts-504-fix.md.
    const query = collectionQuery(params.query);
    const response = query
        ? await api.userThoughts(
              params.username,
              params.mode,
              params.orderingStyle,
              params.paginationParameter,
              undefined,
              query,
          )
        : await api.userThoughts(
              params.username,
              params.mode,
              params.orderingStyle,
              params.paginationParameter,
          );
    return response.data ?? [];
}

export async function fetchFollowersPage(
    api: ProfilePagePaginationApi,
    username: string,
    paginationParameter?: string,
): Promise<UserProfileDTO[]> {
    const response = await api.newFollowersForUser(username, paginationParameter);
    return response.data ?? [];
}

export async function fetchFollowingsPage(
    api: ProfilePagePaginationApi,
    username: string,
    paginationParameter?: string,
): Promise<UserProfileDTO[]> {
    const response = await api.newFollowingsForUser(username, paginationParameter);
    return response.data ?? [];
}

export async function fetchUserGroupsPage(
    api: ProfilePagePaginationApi,
    params: {
        username: string;
        scope: UserGroupScope;
        orderingStyle?: string;
        visibility: UserGroupsVisibility;
        paginationParameter?: string;
        languageScope?: UserDiscussionLanguageScope;
        /** `DASHBOARD-23` — matched server-side on the group's name and description. */
        query?: string;
    },
): Promise<GroupDTO[]> {
    // languageScope is a first-class client parameter (it stopped needing a raw-axios override when
    // the generated client was refreshed on 2026-07-21). Only sent when narrowing to "mine".
    const query = collectionQuery(params.query);
    const narrowingLanguage = isNarrowingLanguageScope(params.languageScope);
    // See fetchUserDiscussionsPage for why the unnarrowed call keeps its exact argument list.
    const response = query
        ? await api.userGroups(
              params.username,
              params.scope,
              params.orderingStyle,
              params.visibility,
              params.paginationParameter,
              undefined,
              narrowingLanguage ? params.languageScope : undefined,
              query,
          )
        : narrowingLanguage
            ? await api.userGroups(
                  params.username,
                  params.scope,
                  params.orderingStyle,
                  params.visibility,
                  params.paginationParameter,
                  undefined,
                  params.languageScope,
              )
            : await api.userGroups(
                  params.username,
                  params.scope,
                  params.orderingStyle,
                  params.visibility,
                  params.paginationParameter,
              );
    return response.data ?? [];
}

export async function fetchUserSeriesPage(
    api: ProfilePagePaginationApi,
    params: {
        ownerUsername: string;
        orderingStyle?: string;
        visibility: UserSeriesVisibility;
        paginationParameter?: string;
        languageScope?: UserDiscussionLanguageScope;
        /** `DASHBOARD-23` — matched server-side on the series' title and description. */
        query?: string;
    },
): Promise<SeriesDTO[]> {
    // See fetchUserGroupsPage: typed client parameter since the 2026-07-21 client refresh.
    const query = collectionQuery(params.query);
    const narrowingLanguage = isNarrowingLanguageScope(params.languageScope);
    // See fetchUserDiscussionsPage for why the unnarrowed call keeps its exact argument list.
    const response = query
        ? await api.userSeries(
              params.ownerUsername,
              params.orderingStyle,
              params.visibility,
              params.paginationParameter,
              undefined,
              narrowingLanguage ? params.languageScope : undefined,
              query,
          )
        : narrowingLanguage
            ? await api.userSeries(
                  params.ownerUsername,
                  params.orderingStyle,
                  params.visibility,
                  params.paginationParameter,
                  undefined,
                  params.languageScope,
              )
            : await api.userSeries(
                  params.ownerUsername,
                  params.orderingStyle,
                  params.visibility,
                  params.paginationParameter,
              );
    return response.data ?? [];
}

export async function fetchSeriesDiscussionsPage(
    api: ProfilePagePaginationApi,
    params: {
        seriesPublicId: string;
        paginationParameter?: string;
        orderingStyle?: string;
    },
): Promise<DiscussionWithoutContentDTOInSeries[]> {
    const response = await api.discussionsInSeries(params.seriesPublicId, params.paginationParameter, params.orderingStyle);
    return response.data ?? [];
}

/**
 * Bookmarks series ids resolved during this page session, keyed by owner username.
 *
 * <p>The scan below is O(series pages) and, because it orders newest-first while the bookmarks series is
 * created at signup, it is *worst*-case for exactly the members who have used the product longest — the
 * cause behind `DASHBOARD-2`, where the Saved tab never resolved in any of six captures. Since 2026-08-14
 * `my_dashboard.jte` publishes the id the server already had, and the seed is written here so a later
 * soft navigation (whose dataset is built in the browser and carries no server value) reuses it instead
 * of scanning again.
 *
 * <p>Safe to keep for the life of the tab: the bookmarks series is created at signup and
 * `SeriesService.ensureBookmarksSeriesForUser` guarantees it keeps existing, so the id does not change.
 * A miss simply falls through to the scan.
 */
const bookmarksSeriesPublicIdByOwner = new Map<string, string>();

export function rememberBookmarksSeriesPublicId(ownerUsername: string, seriesPublicId: string | null | undefined): void {
    if (!ownerUsername || !seriesPublicId) {
        return;
    }
    bookmarksSeriesPublicIdByOwner.set(ownerUsername, seriesPublicId);
}

export function getRememberedBookmarksSeriesPublicId(ownerUsername: string): string | undefined {
    return ownerUsername ? bookmarksSeriesPublicIdByOwner.get(ownerUsername) : undefined;
}

/** Test-only: the memo is module state, so a spec that seeds it must be able to clear it. */
export function clearRememberedBookmarksSeriesPublicIds(): void {
    bookmarksSeriesPublicIdByOwner.clear();
}

export async function findBookmarksSeriesPublicId(
    api: ProfilePagePaginationApi,
    ownerUsername: string,
): Promise<string | undefined> {
    const remembered = getRememberedBookmarksSeriesPublicId(ownerUsername);
    if (remembered) {
        return remembered;
    }

    const seenPaginationParameters = new Set<string>();
    let paginationParameter: string | undefined = undefined;

    while (true) {
        const page = await fetchUserSeriesPage(api, {
            ownerUsername,
            orderingStyle: "new",
            visibility: "all",
            paginationParameter,
        });

        const bookmarksSeries = page.find((series) => isBookmarksSeries(series));
        if (bookmarksSeries?.publicId) {
            rememberBookmarksSeriesPublicId(ownerUsername, bookmarksSeries.publicId);
            return bookmarksSeries.publicId;
        }

        const nextPaginationParameter = page[page.length - 1]?.paginationParameter ?? undefined;
        if (page.length === 0 || !nextPaginationParameter || seenPaginationParameters.has(nextPaginationParameter)) {
            return undefined;
        }

        seenPaginationParameters.add(nextPaginationParameter);
        paginationParameter = nextPaginationParameter;
    }
}
